import React, { Suspense, useRef, useMemo, useState, useEffect } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, Float } from "@react-three/drei";

function DragonModel({ cfg }) {
  const { scene } = useGLTF(cfg.glb_url);
  const ref = useRef();

  useMemo(() => {
    const color = new THREE.Color(cfg.color || "#D4AF37");
    scene.traverse((o) => {
      if (o.isMesh && o.material) {
        o.material = o.material.clone();
        o.material.color = color;
        o.material.emissive = color;
        o.material.emissiveIntensity = cfg.emissive ?? 0.35;
        o.material.metalness = 0.85;
        o.material.roughness = 0.3;
      }
    });
  }, [scene, cfg.color, cfg.emissive]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const baseRot = cfg.rotation || [0, 0, 0];
    if (cfg.effect === "orbit") {
      ref.current.rotation.y = baseRot[1] + t * 0.4;
    } else if (cfg.effect === "static") {
      ref.current.rotation.set(...baseRot);
    } else {
      ref.current.rotation.y = baseRot[1] + Math.sin(t * 0.3) * 0.2;
    }
  });

  return (
    <primitive
      ref={ref}
      object={scene}
      position={cfg.position || [0, 0, 0]}
      scale={cfg.scale || 1}
      rotation={cfg.rotation || [0, 0, 0]}
    />
  );
}

export function shouldReduceFx(cfg) {
  if (typeof window === "undefined") return true;
  const mobile = window.matchMedia("(max-width: 768px)").matches;
  const weak = (navigator.hardwareConcurrency || 8) <= 4;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return true;
  if (mobile && !cfg.show_mobile) return true;
  if (!mobile && cfg.show_desktop === false) return true;
  if (weak && mobile) return true;
  return false;
}

export default function Dragon3D({ cfg = {}, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (failed || !cfg.glb_url) return null;
  return (
    <div className={className} data-testid="dragon-3d-canvas">
      <Canvas
        camera={{ position: [0, 0.4, 4.2], fov: 42 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
        onCreated={(state) => {
          state.gl.domElement.addEventListener("webglcontextlost", () => setFailed(true));
        }}
      >
        <ambientLight intensity={0.55} />
        <directionalLight position={[3, 4, 5]} intensity={1.5} color="#E6C25A" />
        <pointLight position={[-3, -2, -3]} intensity={0.7} color="#8B0000" />
        <Suspense fallback={null}>
          {cfg.effect === "float" ? (
            <Float speed={2} rotationIntensity={0.35} floatIntensity={1.1}>
              <DragonModel cfg={cfg} />
            </Float>
          ) : (
            <DragonModel cfg={cfg} />
          )}
        </Suspense>
      </Canvas>
    </div>
  );
}
